<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import {computed} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpCardReviewRating from '~/components/cp/CpCardReview/CpCardReviewRating/CpCardReviewRating.vue';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import CpCardReviewClassification from
    '~/components/cp/CpCardReview/CpCardReviewClassification/CpCardReviewClassification.vue';
  import CpCardRedirectReviews from '~/components/cp/CpCardRedirectReviews/CpCardRedirectReviews.vue';
  import CpButton from '~/components/cp/CpButton/CpButton.vue';
  import {usePdp} from '~/stores/detailsStore';
  import CpNuxtLink from '~/components/cp/CpNuxtLink/CpNuxtLink.vue';
  import {useGetArticleReviewByUser, useGetArticleReviews} from '~/composables/queries/useReviews';

  const props = defineProps<{
    reviewsToShow: number,
    full?: boolean,
    isLogged?: boolean
  }>();

  const store = usePdp();
  const userReviews = ref<ReviewIdViewModel>();

  /** Get all reviews data */
  const {data: usefulReviewsResponse} = await useGetArticleReviews({
    articleId: store.getProduct!.id,
    pageNumber: 1,
    pageSize: props.reviewsToShow,
    sortType: 1,
  });
  const {data: recentReviewsResponse} = await useGetArticleReviews({
    articleId: store.getProduct!.id,
    pageNumber: 1,
    pageSize: props.reviewsToShow,
    sortType: 2,
  });

  if (store.getProduct) {
    const {data: userReviewsResponse} = await useGetArticleReviewByUser({productId: store.getProduct.id});
    if (userReviewsResponse.value) {
      userReviews.value = userReviewsResponse.value;
    }
  }

  const ratings = computed(() => {
    const rating = store.getProduct.rating;
    return [
      {stars: 5, total: rating.star5 / rating.total},
      {stars: 4, total: rating.star4 / rating.total},
      {stars: 3, total: rating.star3 / rating.total},
      {stars: 2, total: rating.star2 / rating.total},
      {stars: 1, total: rating.star1 / rating.total},
    ];
  });
  const productReviewed = computed(() => !!(userReviews && userReviews.value && userReviews.value.data));

  const {b, e} = useCpBem('cp-section-pdp-reviews');
  const {t} = useI18n();
</script>

<template>
    <div :class="b">
        <CpSectionHeader
            :title="t('costumerOpinions')"
            type="h2"
            show-top-border
        />
        <CpCardReviewRating
            v-if="store.getProduct && Number(store.getProduct.rating.global)"
            :ratings="ratings"
            :total-rate="store.getProduct.rating.global"
            :shown-reviews="store.getProduct.rating.total > 4 ? reviewsToShow : store.getProduct.rating.total "
            :reviews-number="store.getProduct.rating.total"
        />
        <div :class="e('content')">
            <CpCardReviewClassification
                v-if="usefulReviewsResponse"
                type="MostUseful"
                :reviews="usefulReviewsResponse.data"
                :is-logged="isLogged"
            />
            <CpCardReviewClassification
                v-if="recentReviewsResponse"
                type="MostRecent"
                :reviews="recentReviewsResponse.data"
                :is-logged="isLogged"
            />
            <CpCardRedirectReviews :reviewed="productReviewed" />
            <CpNuxtLink
                v-if="recentReviewsResponse && recentReviewsResponse?.data.length"
                page-name="details-all-reviews"
                :link="store.getProduct.allReviewsLink"
            >
                <CpButton
                    :label="t('seeAllOpinions')"
                    kind="label"
                    color="primary"
                    hollow
                    full-width
                    type="button"
                />
            </CpNuxtLink>
        </div>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global";
  .cp-section-pdp-reviews {
    display: flex;
    flex-direction: column;
    gap: $cp-gutter;
    justify-content: center;
    &__content {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter;
      padding: 0 $cp-gutter $cp-gutter;
    }
    &__rating {
      padding-bottom: $cp-gutter;
      border-bottom: $cp-border-standard;
    }
  }
</style>
<i18n locale="es-US" lang="yaml">
  costumerOpinions: "Opiniones de clientes"
  seeAllOpinions: "Ver todas las opiniones"
</i18n>
